import {QuantityRange} from '@hconnect/tuc-processes/fields'
import {useCallback} from 'react'

import {useNumberStepCalculator, NumberStepCalculator} from '../utils/NumberStepCalculator'

interface UseQuantityValidationParams {
  range: QuantityRange
  value: number | null
  defaultValue?: number
  onAutoCorrect?: (correction: {
    originalValue: number
    correctedValue: number
    reason: 'step' | 'min' | 'max'
  }) => void
}

interface UseQuantityValidationReturn {
  calculator: NumberStepCalculator
  validateAndAdjust: (newValue: number, source: 'input' | 'increment' | 'decrement') => number
  getNextValue: (currentValue: number) => number | undefined
  getPreviousValue: (currentValue: number) => number | undefined
  formatValue: (value: number) => string
}

/**
 * Hook that handles validation, auto-correction, and step-based calculations
 * for quantity inputs. Encapsulates the complex mathematical logic.
 */
export const useQuantityValidation = ({
  range,
  value,
  onAutoCorrect
}: UseQuantityValidationParams): UseQuantityValidationReturn => {
  const {min, max, step, default: defaultValue, closeAt} = range

  // Initialize the number step calculator for precise numeric operations
  const calculator = useNumberStepCalculator({
    minValue: min,
    maxValue: max,
    stepValue: step,
    initialValue: value || defaultValue,
    extraValues: closeAt !== undefined ? [closeAt] : undefined
  })

  // Validate and adjust the input value
  const validateAndAdjust = useCallback(
    (newValue: number, source: 'input' | 'increment' | 'decrement'): number => {
      let adjustedValue: number
      let correctionReason: 'step' | 'min' | 'max' | null = null

      if (source === 'input') {
        // For manual input, validate against all constraints
        if (newValue < min && newValue !== closeAt) {
          adjustedValue = min
          correctionReason = 'min'
        } else if (newValue > max && newValue !== closeAt) {
          adjustedValue = max
          correctionReason = 'max'
        } else if (!calculator.validateValue(newValue)) {
          adjustedValue = calculator.getClosestValueInRange(newValue)
          correctionReason = 'step'
        } else {
          adjustedValue = newValue
        }
      } else {
        // For increment/decrement, the value should already be valid
        adjustedValue = newValue
      }

      // Trigger auto-correction callback if value was adjusted
      if (correctionReason && onAutoCorrect && newValue !== adjustedValue) {
        onAutoCorrect({
          originalValue: newValue,
          correctedValue: adjustedValue,
          reason: correctionReason
        })
      }

      return adjustedValue
    },
    [min, max, closeAt, calculator, onAutoCorrect]
  )

  // Get next valid value
  const getNextValue = useCallback(
    (currentValue: number) => {
      return calculator.getNextValue(currentValue)
    },
    [calculator]
  )

  // Get previous valid value
  const getPreviousValue = useCallback(
    (currentValue: number) => {
      return calculator.getPreviousValue(currentValue)
    },
    [calculator]
  )

  // Format value with correct number of decimals
  const formatValue = useCallback(
    (valueToFormat: number) => {
      return valueToFormat.toFixed(calculator.numberOfDecimals)
    },
    [calculator]
  )

  return {
    calculator,
    validateAndAdjust,
    getNextValue,
    getPreviousValue,
    formatValue
  }
}
